import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Instagram, ArrowUpRight, Mail, Play, ChevronRight } from "lucide-react";
import "@/App.css";

const THUMBNAIL_DATA = [
  {
    id: "gen-z-audience",
    number: "01",
    title: "Crafted for a Gen Z Audience",
    image: "https://images.pexels.com/photos/6489046/pexels-photo-6489046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    imageAlt: "Gaming setup with neon lights",
  },
  {
    id: "discover-tmu-faculty",
    number: "02",
    title: "Discover TMU: Professor & Faculty Storytelling",
    image: "https://customer-assets.emergentagent.com/job_portfolio-redesign-28/artifacts/mibbizpw_Artboard%20%E2%80%93%201.png",
    imageAlt: "TMU Faculty Stories",
  },
  {
    id: "student-stories",
    number: "03",
    title: "Reframing Admissions Through Authentic Student Stories",
    image: "https://customer-assets.emergentagent.com/job_portfolio-redesign-28/artifacts/i9zznszi_DSC09484.jpg",
    imageAlt: "Students sitting on campus steps",
  },
  {
    id: "cultural-moments",
    number: "04",
    title: "Cultural Moments and Trend-Driven Content",
    image: "https://customer-assets.emergentagent.com/job_portfolio-redesign-28/artifacts/l0drv9fk_Sequence%20010%20%281%29.png",
    imageAlt: "TMU mascot at event",
  },
];

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const IGEmbed = ({ url, testId }) => {
  const embedUrl = url.endsWith("/") ? `${url}embed/` : `${url}/embed/`;
  return (
    <div className="ig-embed-wrap" data-testid={testId}>
      <iframe
        src={embedUrl}
        className="ig-embed-iframe"
        frameBorder="0"
        scrolling="no"
        allowTransparency="true"
        allow="encrypted-media"
        title="Instagram Reel"
      />
    </div>
  );
};

const IGPlaceholder = ({ testId }) => (
  <div className="ig-placeholder" data-testid={testId}>
    <div className="ig-phone-frame">
      <div className="ig-phone-notch" />
      <div className="ig-phone-screen">
        <div className="ig-play-btn">
          <Play size={20} fill="white" color="white" />
        </div>
        <div className="ig-phone-bottom">
          <Instagram size={16} className="ig-icon-small" strokeWidth={1.5} />
          <span className="ig-label-text">Reel</span>
        </div>
      </div>
    </div>
  </div>
);

const Navigation = () => (
  <nav className="nav-bar" data-testid="navigation">
    <div className="nav-inner">
      <a href="#top" className="nav-logo" data-testid="nav-logo">Portfolio</a>
      <ul className="nav-links">
        <li><a href="#projects" className="nav-link" data-testid="nav-link-projects">Projects</a></li>
        <li><a href="#about" className="nav-link" data-testid="nav-link-about">About</a></li>
        <li style={{ display: "none" }}><a href="#services" className="nav-link" data-testid="nav-link-services">Services</a></li>
        <li><a href="#contact" className="nav-link" data-testid="nav-link-contact">Contact</a></li>
      </ul>
    </div>
  </nav>
);

const Hero = () => (
  <section className="hero" id="top" data-testid="hero-section">
    <FadeIn>
      <p className="hero-label">Creative Strategist & Content Producer</p>
    </FadeIn>
    <FadeIn delay={0.1}>
      <h1 className="hero-title">
        Recent<br /><span className="accent-word">Projects</span>
      </h1>
    </FadeIn>
    <FadeIn delay={0.2}>
      <p className="hero-description">
        A curated selection of campaign work spanning social strategy, content production
        and cultural storytelling. Each project is built around a clear brief, a sharp strategy
        and measurable outcomes.
      </p>
    </FadeIn>
  </section>
);

const ProjectThumbnails = () => (
  <section className="thumbnails-section" data-testid="project-thumbnails">
    <div className="thumbnails-grid">
      {THUMBNAIL_DATA.map((t, i) => (
        <FadeIn key={t.id} delay={i * 0.08}>
          <a href={`#campaign-${t.id}`} className="thumbnail-card" data-testid={`thumbnail-${t.id}`}>
            <div className="thumbnail-image-wrap">
              <img src={t.image} alt={t.imageAlt} loading="lazy" />
              <div className="thumbnail-overlay">
                <span className="thumbnail-num">{t.number}</span>
                <ArrowUpRight size={18} />
              </div>
            </div>
            <div className="thumbnail-info">
              <h3 className="thumbnail-title">{t.title}</h3>
            </div>
          </a>
        </FadeIn>
      ))}
    </div>
  </section>
);

const CampaignHeader = ({ id, number, title, byline, prefix }) => (
  <FadeIn>
    <div className="campaign-header" data-testid={`campaign-header-${id}`}>
      <span className="campaign-number" data-testid={`campaign-number-${id}`}>{prefix || "Campaign"} {number}</span>
      <div className="campaign-header-text">
        <h2 className="campaign-title" data-testid={`campaign-title-${id}`}>{title}</h2>
        {byline && <p className="campaign-byline" data-testid={`campaign-byline-${id}`}>{byline}</p>}
      </div>
    </div>
  </FadeIn>
);

const MetricsStrip = () => null; // removed

/* ─── Who I Am ─── */
const WhoIAm = () => (
  <section className="about-section" id="about" data-testid="who-i-am-section">
    <FadeIn>
      <div className="about-inner">
        <div className="about-photo-box" data-testid="about-photo-box">
          <img
            src="https://customer-assets.emergentagent.com/job_portfolio-redesign-28/artifacts/646riy73_T0418873FK4-U05RA15GDK7-67853aa887c5-512%20%281%29.jpeg"
            alt="Caroline"
            data-testid="about-photo"
          />
        </div>
        <div className="about-text">
          <span className="editorial-label" data-testid="about-label">Who I Am</span>
          <h2 className="about-title" data-testid="about-title">Caroline</h2>
          <p className="about-body" data-testid="about-body-1">
            I'm a digital marketer and creative strategist helping makers, educators and small businesses bring their stories to life online.
          </p>
          <p className="about-body" data-testid="about-body-2">
            I'm a lifelong learner who's always eager to develop new ideas, test new tools and find smarter, more creative ways to communicate ideas.
          </p>
        </div>
      </div>
    </FadeIn>
  </section>
);

const TextBlock = ({ label, testId, children }) => (
  <div className="editorial-text-block">
    <span className="editorial-label" data-testid={`${testId}-label`}>{label}</span>
    <div className="editorial-body" data-testid={`${testId}-body`}>{children}</div>
  </div>
);

/* ─── Campaign 01: Crafted for a Gen Z Audience ─── */
const CampaignGenZ = () => (
  <section className="campaign-editorial" id="campaign-gen-z-audience" data-testid="campaign-section-gen-z-audience">
    <CampaignHeader id="gen-z-audience" number="01" title="Crafted for a Gen Z Audience" />
    <FadeIn delay={0.05}>
      <div className="editorial-hero">
        <img src="https://images.pexels.com/photos/6489046/pexels-photo-6489046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Gaming setup with neon lights" data-testid="campaign-image-gen-z-audience" />
      </div>
    </FadeIn>
    <div className="editorial-two-col">
      <FadeIn delay={0.1}>
        <TextBlock label="The Ask" testId="the-ask-gen-z-audience">
          <p>Toronto Metropolitan University Open House content needed a refresh. Traditional promotional content wasn't cutting through with Gen Z audiences on social.</p>
        </TextBlock>
      </FadeIn>
      <FadeIn delay={0.15}>
        <TextBlock label="The Approach" testId="the-approach-gen-z-audience">
          <p>I developed a gaming-inspired creative strategy rooted in Minecraft aesthetics, reframing Open House as an interactive, choice-driven experience aligned with Gen Z digital behaviours.</p>
        </TextBlock>
      </FadeIn>
    </div>
    <FadeIn delay={0.2}>
      <div className="editorial-reels-row" data-testid="reels-row-gen-z-audience">
        <div className="editorial-reel-item">
          <IGEmbed url="https://www.instagram.com/p/DWCOko2gmdD/" testId="campaign-gen-z-audience-ig-embed-1" />
        </div>
        <div className="editorial-reel-item">
          <IGEmbed url="https://www.instagram.com/p/DWO0BDKgmyE/" testId="campaign-gen-z-audience-ig-embed-2" />
        </div>
      </div>
    </FadeIn>
    <div className="editorial-two-col">
      <FadeIn delay={0.25}>
        <TextBlock label="The Build" testId="the-build-gen-z-audience">
          <p>I partnered with a student creator to bring the concept to life through short-form video. Layering campus footage with custom motion graphics, game UI elements and sound design helped create something that felt instantly familiar (and scroll-stopping) to our target demographic.</p>
        </TextBlock>
      </FadeIn>
      <FadeIn delay={0.3}>
        <TextBlock label="The Outcome" testId="the-outcome-gen-z-audience">
          <p>I led the creation of three short-form videos and static graphics to use in the promotion and recap of the event. The videos used in the lead-up organically reached +36k views. One video alone generated 1,496 interactions and 828 shares, clear signals of strong peer-to-peer distribution.</p>
        </TextBlock>
      </FadeIn>
    </div>
  </section>
);

/* ─── Campaign 02: Discover TMU Faculty ─── */
const CampaignTMU = () => (
  <section className="campaign-editorial" id="campaign-discover-tmu-faculty" data-testid="campaign-section-discover-tmu-faculty">
    <CampaignHeader id="discover-tmu-faculty" number="02" title="Discover TMU: Professor & Faculty Storytelling" />
    <FadeIn delay={0.05}>
      <div className="editorial-hero">
        <img src="https://customer-assets.emergentagent.com/job_portfolio-redesign-28/artifacts/mibbizpw_Artboard%20%E2%80%93%201.png" alt="TMU Faculty Stories" data-testid="campaign-image-discover-tmu-faculty" />
      </div>
    </FadeIn>
    <div className="editorial-two-col">
      <FadeIn delay={0.1}>
        <TextBlock label="The Ask" testId="the-ask-discover-tmu-faculty">
          <p>Develop a scalable content series that showcases academic programs and differentiates TMU by highlighting the value of its faculty, courses and real-world learning experiences.</p>
        </TextBlock>
      </FadeIn>
      <FadeIn delay={0.15}>
        <TextBlock label="The Approach" testId="the-approach-discover-tmu-faculty">
          <p>Built a faculty-led content strategy designed to inspire prospective students at the top of the funnel, while also supporting decision-making closer to application and confirmation.</p>
          <p>Shifted from broad institutional messaging to program-specific storytelling, using professors as credible, engaging voices to communicate what students would actually learn and experience.</p>
        </TextBlock>
      </FadeIn>
    </div>
    <FadeIn delay={0.2}>
      <div className="editorial-reels-row editorial-reels-three" data-testid="reels-row-discover-tmu-faculty">
        <div className="editorial-reel-item">
          <IGEmbed url="https://www.instagram.com/reel/DAq_YuvvSs0/" testId="campaign-discover-tmu-faculty-ig-embed-1" />
        </div>
        <div className="editorial-reel-item">
          <IGEmbed url="https://www.instagram.com/reel/DFNoj54unPB/" testId="campaign-discover-tmu-faculty-ig-embed-2" />
        </div>
        <div className="editorial-reel-item">
          <IGEmbed url="https://www.instagram.com/reel/DCrqcnsPdZf/" testId="campaign-discover-tmu-faculty-ig-embed-3" />
        </div>
      </div>
    </FadeIn>
    <FadeIn delay={0.25}>
      <div className="editorial-single-block">
        <TextBlock label="The Build" testId="the-build-discover-tmu-faculty">
          <p>Produced a series of short-form videos featuring professors across all six faculties, each highlighting a specific course, research area or student opportunity.</p>
          <p>Content followed a repeatable format, combining concise, scripted voiceover with dynamic b-roll of labs, classrooms and student interaction to ensure clarity, consistency and scalability across programs.</p>
        </TextBlock>
      </div>
    </FadeIn>
    <FadeIn delay={0.3}>
      <div className="editorial-single-block">
        <TextBlock label="Production" testId="production-discover-tmu-faculty">
          <p>Executed end-to-end production across a 33-video series within a lean two-person team, managing scripting, filming, directing and post-production.</p>
          <p>Partnered closely with faculty stakeholders to coordinate filming and approvals, while maintaining an efficient production workflow and consistent creative standard across all outputs.</p>
        </TextBlock>
      </div>
    </FadeIn>
    <FadeIn delay={0.35}>
      <div className="editorial-results" data-testid="results-discover-tmu-faculty">
        <p>The series generated 1.2M+ views across Instagram, TikTok and YouTube Shorts, and was deployed across both organic and paid social strategies to maximize reach and performance.</p>
        <p>Faculty partners actively amplified the content through their own channels, extending reach and strengthening program-level visibility.</p>
      </div>
    </FadeIn>
  </section>
);

/* ─── Campaign 03: Student Stories ─── */
const CampaignStudentStories = () => (
  <section className="campaign-editorial" id="campaign-student-stories" data-testid="campaign-section-student-stories">
    <CampaignHeader id="student-stories" number="03" title="Reframing Admissions Through Authentic Student Stories" />
    <FadeIn delay={0.05}>
      <div className="editorial-hero">
        <img
          src="https://customer-assets.emergentagent.com/job_portfolio-redesign-28/artifacts/i9zznszi_DSC09484.jpg"
          alt="Students sitting on campus steps"
          data-testid="campaign-image-student-stories"
        />
      </div>
    </FadeIn>
    <FadeIn delay={0.1}>
      <div className="editorial-single-block">
        <TextBlock label="The Ask" testId="the-ask-student-stories">
          <p>Create a relatable, student-first content series that builds trust with prospective applicants beyond traditional testimonials.</p>
        </TextBlock>
      </div>
    </FadeIn>
    <FadeIn delay={0.15}>
      <div className="editorial-single-block">
        <TextBlock label="The Approach" testId="the-approach-student-stories">
          <p>Moved away from traditional testimonials in favour of a documentary-style series, pairing real student voices with faculty and program context.</p>
          <p>Content was informed by social listening and recurring questions from prospective students, guiding which experiences and moments we chose to highlight.</p>
        </TextBlock>
      </div>
    </FadeIn>
    <FadeIn delay={0.2}>
      <div className="editorial-reels-row editorial-reels-three" data-testid="reels-row-student-stories">
        <div className="editorial-reel-item">
          <IGEmbed url="https://www.instagram.com/reel/DPy7OxujbLk/" testId="campaign-student-stories-ig-embed-1" />
        </div>
        <div className="editorial-reel-item">
          <IGEmbed url="https://www.instagram.com/p/DVgmLP9DZol/" testId="campaign-student-stories-ig-embed-2" />
        </div>
        <div className="editorial-reel-item">
          <IGEmbed url="https://www.instagram.com/p/DWRakLggkQJ/" testId="campaign-student-stories-ig-embed-3" />
        </div>
      </div>
    </FadeIn>
    <FadeIn delay={0.25}>
      <div className="editorial-single-block">
        <TextBlock label="The Build" testId="the-build-student-stories">
          <p>Developed an on-going vertical video series where Toronto Metropolitan University students shared their experiences and insights on classes, labs, work and community experiences.</p>
          <p>Maintained a consistent visual language to ensure the content felt natural and platform-native, while still delivering clarity through light scripting and structured storytelling.</p>
        </TextBlock>
      </div>
    </FadeIn>
    <FadeIn delay={0.3}>
      <div className="editorial-single-block">
        <TextBlock label="Production" testId="production-student-stories">
          <p>I executed end-to-end production as part of a lean two-person team, managing creative direction, interviewing, scripting and post-production across the series.</p>
        </TextBlock>
      </div>
    </FadeIn>
    <FadeIn delay={0.35}>
      <div className="editorial-results" data-testid="results-student-stories">
        <p>The series generated 240K+ video views and became the highest-performing organic content on Instagram, with a 62% average watch-through rate.</p>
        <p>Top-performing content was further leveraged in paid campaigns, extending reach and reinforcing its effectiveness across both organic and paid channels.</p>
        <p>Content was also repurposed across the admissions website and recruitment presentations, supporting multiple stages of the enrolment funnel.</p>
      </div>
    </FadeIn>
  </section>
);

/* ─── Case Study 04: Cultural Moments ─ Slideshow ─── */
const SLIDESHOW_ITEMS = [
  { id: 1, url: "https://www.instagram.com/reel/DUJB5FPEfO_/", caption: "Trend response \u2014 24hr turnaround" },
  { id: 2, url: "https://www.instagram.com/p/DP4EuX8jejI/", caption: "Community-driven storytelling" },
  { id: 3, url: "https://www.instagram.com/p/DUn3i6IkYte/", caption: "Platform-native format" },
  { id: 4, url: "https://www.instagram.com/p/DCSECygP0Or/", caption: "Brand voice meets trend" },
];

const CampaignCulturalMoments = () => {
  const trackRef = useRef(null);
  const scroll = (dir) => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: dir * 320, behavior: "smooth" });
    }
  };
  return (
    <section className="campaign-editorial campaign-slideshow-section" id="campaign-cultural-moments" data-testid="campaign-section-cultural-moments">
      <CampaignHeader id="cultural-moments" number="04" title="Cultural Moments and Trend-Driven Content" prefix="Case Study" />
      <FadeIn delay={0.1}>
        <div className="slideshow-controls">
          <button className="slideshow-arrow" onClick={() => scroll(-1)} data-testid="slideshow-prev" aria-label="Previous">
            <ChevronRight size={20} style={{ transform: "rotate(180deg)" }} />
          </button>
          <button className="slideshow-arrow" onClick={() => scroll(1)} data-testid="slideshow-next" aria-label="Next">
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="slideshow-track" ref={trackRef} data-testid="slideshow-track">
          {SLIDESHOW_ITEMS.map((item) => (
            <div className="slideshow-item" key={item.id} data-testid={`slideshow-item-${item.id}`}>
              <div className="slideshow-embed-wrap">
                <IGEmbed url={item.url} testId={`slideshow-embed-${item.id}`} />
              </div>
              <p className="slideshow-caption" data-testid={`slideshow-caption-${item.id}`}>{item.caption}</p>
            </div>
          ))}
        </div>
      </FadeIn>
    </section>
  );
};

/* ─── Featured Work: Metropolitan ─── */
const FeaturedMetropolitan = () => (
  <section className="featured-section" data-testid="featured-metropolitan">
    <div className="featured-inner">
      <FadeIn>
        <div className="featured-header">
          <span className="featured-tag" data-testid="featured-tag">Featured</span>
          <h2 className="featured-title" data-testid="featured-title">
            What does it mean<br />to be metropolitan?
          </h2>
          <p className="featured-subtitle" data-testid="featured-subtitle">
            Toronto Metropolitan University / Brand Film
          </p>
        </div>
      </FadeIn>
      <FadeIn delay={0.1}>
        <div className="featured-video-wrap" data-testid="featured-video">
          <iframe
            src="https://www.youtube.com/embed/RBnu_DdEFHY?rel=0&modestbranding=1"
            title="What does it mean to be metropolitan?"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="featured-video-iframe"
          />
        </div>
      </FadeIn>
      <FadeIn delay={0.15}>
        <div className="featured-credits" data-testid="featured-credits">
          <div className="credit-item">
            <span className="credit-label">Role</span>
            <span className="credit-value">Visual Direction / Storyboard / Producer</span>
          </div>
          <div className="credit-item">
            <span className="credit-label">Responsibilities</span>
            <span className="credit-value">Managed creative direction, production and talent</span>
          </div>
          <div className="credit-item">
            <span className="credit-label">Client</span>
            <span className="credit-value">Toronto Metropolitan University</span>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);

const Footer = () => (
  <footer className="site-footer" id="contact" data-testid="footer">
    <div className="footer-inner">
      <span className="footer-text" data-testid="footer-copyright">2025 Portfolio. All rights reserved.</span>
      <div className="footer-links">
        <a href="mailto:hello@example.com" className="footer-link" data-testid="footer-email-link">
          <Mail size={14} style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} /> Contact
        </a>
        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="footer-link" data-testid="footer-instagram-link">
          <Instagram size={14} style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} /> Instagram
        </a>
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <div className="App" data-testid="app-root">
      <Navigation />
      <Hero />
      <ProjectThumbnails />

      <div id="projects" data-testid="recent-projects-section">
        <div className="section-header">
          <FadeIn>
            <p className="section-label">Selected Work</p>
            <h2 className="section-title" data-testid="recent-projects-title">Recent Projects</h2>
          </FadeIn>
        </div>

        <CampaignGenZ />
        <CampaignTMU />
        <CampaignStudentStories />
        <CampaignCulturalMoments />
      </div>

      <FeaturedMetropolitan />
      <WhoIAm />
      <Footer />
    </div>
  );
}

export default App;
